import React, {useRef, useState} from 'react';

import {get} from 'lodash-es';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import Measure from 'react-measure';
import {useSelector} from 'react-redux';

import ExternalToolIndicator from 'containers/ExternalToolIndicator/ExternalToolIndicator';
import {pageElements as types} from 'modulesV2/constants';
import {isSpecifiedMode, getReadPageMode} from 'modulesV2/selectors';
import {
  setContentOffset,
  setGlobalNavHeight as setCssGlobalNavHeight,
  setUtilityNavHeight as setCssUtilityNavHeight,
  setGlobalHeaderHeight
} from 'utils/mastheadHeight';
import {refHeightsDefaults} from 'utils/refHeights';

import {getUser, getUserProperties} from '../../modules/selectors/userSelectors';
import {GlobalLayoutRefsContext} from './GlobalLayoutRefsContext';
import GlobalNav from './GlobalNav';
import {
  headerStyle,
  pinnedHeaderStyle
} from './style';
import UtilityNav from './UtilityNav';

const propTypes = {
  history: PropTypes.object,
  isPresentation: PropTypes.bool,
  isViewingFromAssignment: PropTypes.bool,
  main: PropTypes.node,
  skipToId: PropTypes.string,
};

export const GlobalRefsContextProvider = GlobalLayoutRefsContext.Provider;


const GridLayout = (props) => {
  const globalNavRef = useRef(null);
  const headerRef = useRef(null);
  const utilityNavRef = useRef(null);

  const [pageNavMobileHeight, setPageNavMobileHeight] = useState(0);
  const [globalNavHeight, setGlobalNavHeight] = useState(refHeightsDefaults.globalNavHeight);
  const [utilityNavHeight, setUtilityNavHeight] = useState(refHeightsDefaults.utilityNavHeight);
  const [headerHeight, setHeaderHeight] = useState(refHeightsDefaults.headerHeight);

  const isPresentation = props.isPresentation ? props.isPresentation : useSelector((state) => isSpecifiedMode(state, 'PRESENTATION'));
  const isViewingFromAssignment = props.isViewingFromAssignment ?
    props.isViewingFromAssignment : useSelector((state) => isSpecifiedMode(state, 'VIEW'));
  const readPageMode = useSelector(getReadPageMode);
  const hideMasthead = get(readPageMode.pageElements, types.HIDE_MASTHEAD, false);

  const mastheadDOM = document.getElementById('react-masthead');


  const header = (
    <Measure
      bounds
      onResize={() => {
        const height = 0;
        if (headerHeight !== height) {
          setContentOffset(`${height}px`);
          setGlobalHeaderHeight(`${height}px`);
          setHeaderHeight(height);
        }
      }}
      innerRef={headerRef}
    >
      {({measureRef}) => (
        <header
          ref={measureRef}
          /**
           * During presentation mode and when viewing an assignment, the global navigation is
           * replaced with preview and assignment banners. As of December 2020, global navigation
           * scrolls out of view. To maintain view of banners, keep global navigation (although hidden)
           * pinned for the presentation and viewing assignment modes.
          */
          className={(isPresentation || isViewingFromAssignment) ? pinnedHeaderStyle : headerStyle}
          data-qa-selector={isPresentation ? 'presentation_mode_header' : ''}
        >
          <ExternalToolIndicator />
          <Measure
            bounds
            onResize={(contentRect) => {
              const height = contentRect.bounds.height;
              setCssUtilityNavHeight(`${height}px`);
              if (utilityNavHeight !== height) {
                setUtilityNavHeight(height);
              }
            }}
            innerRef={utilityNavRef}
          >
            {({measureRef}) => (
              <UtilityNav
                hideUtilityNav={hideMasthead}
                skipToId={props.skipToId}
                isViewingFromAssignment={isViewingFromAssignment}
                isPresentation={isPresentation}
                history={props.history}
                ref={measureRef}
              />
            )}
          </Measure>
          <Measure
            bounds
            innerRef={globalNavRef}
            onResize={({bounds}) => {
              setCssGlobalNavHeight(`${bounds.height}px`);
              if (globalNavHeight !== bounds.height) {
                setGlobalNavHeight(bounds.height);
              }
            }}
          >
            {({measureRef}) => (
              <GlobalNav
                hideGlobalNav={isPresentation || isViewingFromAssignment || hideMasthead}
                history={props.history}
                user={useSelector((state) => getUser(state))}
                userProperties={useSelector((state) => getUserProperties(state))}
                ref={measureRef}
              />
            )}
          </Measure>
        </header>
      )}
    </Measure>
  );

  return (
    <GlobalRefsContextProvider
      value={{
        globalNavHeight,
        globalNavRef,
        headerHeight,
        headerRef,
        pageNavMobileHeight,
        setPageNavMobileHeight,
        utilityNavHeight,
        utilityNavRef,
      }}
    >
      <>
        {
          !window.SHOULD_HIDE_MASTHEAD && mastheadDOM &&
              ReactDOM.createPortal(header, mastheadDOM)
        }

        <div
          id="mainContainer"
        >
          {
            props.main
          }
        </div>
      </>
    </GlobalRefsContextProvider>
  );
};

GridLayout.propTypes = propTypes;
export default GridLayout;
